import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { IMenuElement } from '@interfaces/menu.interface';
import { ProgramyStudiowStudentaDoSlubowaniaDto } from '@interfaces/studia/platforma-studenta/slubowanie-program-studiow-studenta.interface';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UsersService } from '@services/api/administracja/user-access/users.service';
import { SlubowanieProgramuStudiowStudentaService } from '@services/api/studia/platforma-studenta/slubowania-programu-studiow-stduenta.service';
import { AuthService } from '@services/auth.service';
import { MenuService } from '@services/menu.service';
import { combineLatest, distinctUntilChanged, filter, Subscription } from 'rxjs';

import { SlubowanieStudentaGuardModalComponent } from './slubowanie-studenta-guard-modal/slubowanie-studenta-guard-modal.component';

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
})
export class MenuComponent implements OnInit, OnDestroy {
  @Output() hideMobileMenu = new EventEmitter<null>();
  @Input() isMobileMenuVisible: boolean;
  readonly userData$ = this.authService.authTokenData$;
  menuElements: IMenuElement[];

  private breakpoint: Subscription;
  private lastSelectedElement: IMenuElement;
  private routerEventJoinPermissions$: Subscription;
  private permissionSub: Subscription;

  constructor(
    readonly authService: AuthService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly menuService: MenuService,
    private readonly permissionsService: UsersService,
    private readonly slubowanieProgramuStudiowStudentaService: SlubowanieProgramuStudiowStudentaService,
    private readonly modalService: NgbModal,
  ) {}

  ngOnInit(): void {
    this.menuService.currentMenuElems$.subscribe((menuElems) => {
      this.menuElements = menuElems.map((item) => ({
        ...item,
        isParent: true,
      }));
    });
    this.handleRouterEvents();
    const qHdBreakpoint = '(min-width: 1024px)';
    this.breakpoint = this.breakpointObserver
      .observe(qHdBreakpoint)
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        if (this.breakpointObserver.isMatched(qHdBreakpoint)) {
          this.isMobileMenuVisible = false;
          this.hideMobileMenu.emit();
        }
      });

    this.checkSlubowanie();
  }

  ngOnDestroy(): void {
    if (this.routerEventJoinPermissions$) {
      this.routerEventJoinPermissions$.unsubscribe();
    }
    if (this.breakpoint) {
      this.breakpoint.unsubscribe();
    }
    if (this.permissionSub) {
      this.permissionSub.unsubscribe();
    }
  }

  private checkSlubowanie(): void {
    this.permissionSub = this.permissionsService.permissions$.subscribe((permissions) => {
      if (
        permissions.some(
          (permission) => permission.code === 'Studia.PlatformaStudenta.ProgramyStudiowStudenta.Slubowanie.Lista',
        )
      ) {
        this.slubowanieProgramuStudiowStudentaService.getRecords().subscribe((res) => {
          if (res.data.length > 0) {
            this.openModal(res.data);
          }
        });
      }
    });
  }

  private openModal(programyStudiowStudentaDoSlubowania: ProgramyStudiowStudentaDoSlubowaniaDto[]): void {
    const modal = this.modalService.open(SlubowanieStudentaGuardModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    (modal.componentInstance as SlubowanieStudentaGuardModalComponent).data = programyStudiowStudentaDoSlubowania;
  }

  toggleChildren(item: IMenuElement): void {
    if (item.isRootElement) {
      return;
    }
    if (item.children?.length) {
      item.hasVisibleChildren = !item.hasVisibleChildren;
      return;
    }
    if (this.lastSelectedElement) {
      this.lastSelectedElement.isActiveRoute = false;
    }
    item.isActiveRoute = true;
    this.lastSelectedElement = item;
  }

  onClickLink(item: IMenuElement): void {
    if (item && item.redirectTo) {
      window.open(item.redirectTo, '_blank');
    }
    this.hideMobileMenu.emit();
  }

  private handleRouterEvents() {
    const routerNavigationEndEvent$ = this.router.events.pipe(filter((val) => val instanceof NavigationEnd));
    const permissions$ = this.permissionsService.permissions$.pipe(filter((val) => !!val));

    this.routerEventJoinPermissions$ = combineLatest([routerNavigationEndEvent$, permissions$]).subscribe(() => {
      if (this.lastSelectedElement) {
        this.lastSelectedElement.isActiveRoute = false;
      }
      const routerNode = this.activatedRoute.snapshot.firstChild;
      this.menuService.setRoleByMenuId(routerNode);

      if (routerNode) {
        const routerNodeData = this.menuService.getDeepestRouteNodeData(routerNode);
        this.openNodePath(routerNodeData.menuId);
      }
    });
  }

  private openNodePath(label: string): void {
    if (label) {
      const nodes = this.menuService.findNodePath(label, this.menuElements, []);
      const selectedNodes = this.findSelectedNodes([...nodes, label]);
      selectedNodes.forEach((item) => {
        if (item.children?.length) {
          item.hasVisibleChildren = true;
          return;
        }
        item.isActiveRoute = true;
        this.lastSelectedElement = item;
      });
    } else {
      this.menuElements = [];
    }
  }

  private findSelectedNodes(nodes: string[]): IMenuElement[] {
    const openedNodes: IMenuElement[] = [];
    for (let index = 0; index < nodes.length; index++) {
      const lastElement = openedNodes[openedNodes.length - 1];
      const arr = !index ? this.menuElements : lastElement.children || [];
      const node = arr.find((item) => item.label === nodes[index]);
      if (!node) {
        return openedNodes;
      }
      openedNodes.push(node);
    }
    return openedNodes;
  }
}
